// frontend/src/components/onboarding/OnboardingCallout.tsx
// The hand-holding shortcut to /demarrage, shown at the top of the inventory
// page — the screen every user lands on.
//
// It is driven by the REAL state of the integration and disappears on its own
// once there is nothing left to guide:
//
//   1. empty platform, no run started  → a prominent card: this is the moment
//      the client would otherwise face three days of manual entry;
//   2. a run is in progress            → a compact resume banner with the
//      current step and what is waiting (validations, questions);
//   3. blocking findings in the audit  → a warning pointing at the report —
//      sales that decrement nothing are not allowed to sit quietly;
//   4. otherwise                       → nothing at all.
//
// It also happens to fix a real gap: the mobile bottom nav has no entry for
// /demarrage, so on a phone this callout is the only visible door in.

import { Link } from 'react-router-dom';
import { Rocket, ArrowRight, AlertOctagon, ClipboardCheck } from 'lucide-react';
import { useOnboardingStatus } from '../../hooks/useOnboarding';
import { STEPS, stageIndex } from './onboardingUtils';

export function OnboardingCallout() {
  const { data, isLoading } = useOnboardingStatus();

  // No flash while loading, and never mistake an unreachable backend (or the
  // offline demo mode) for an empty platform.
  if (isLoading || !data || !data.reachable) return null;

  const { session, inventory, blockingFindings } = data;
  const platformEmpty = inventory.resources === 0 && inventory.recipes === 0;
  const runInProgress =
    session !== null && session.status !== 'COMPLETED' && session.status !== 'ABANDONED';

  // --- 1. Empty platform, nothing started: the big invitation ---------------
  if (platformEmpty && !runInProgress) {
    return (
      <Link
        to="/demarrage"
        className="group block rounded-xl border border-[var(--color-steel)] bg-[var(--color-steel-bg)] px-4 py-4 sm:px-5 transition-all hover:shadow-[0_0_16px_rgba(122,168,200,0.25)]"
      >
        <div className="flex items-center gap-4">
          <div className="hidden sm:flex w-11 h-11 rounded-full bg-[var(--color-steel)] items-center justify-center shrink-0">
            <Rocket className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-base font-semibold text-[var(--foreground)]">
              Votre plateforme est vide — remplissez-la à partir de vos documents
            </p>
            <p className="text-sm text-[var(--muted-foreground)] mt-0.5">
              Déposez vos factures, votre menu et vos recettes : l'intégration assistée construit
              le référentiel produits, les recettes et les coûts. Vous validez, elle saisit.
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[var(--color-steel)] text-white text-sm font-medium shrink-0 group-hover:bg-[var(--color-steel-dark)] transition-colors">
            Commencer
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </Link>
    );
  }

  // --- 2. A run is in progress: compact resume banner -----------------------
  if (runInProgress && session) {
    const step = STEPS[stageIndex(session.stage)];
    const waiting: string[] = [];
    if (session.pendingCount > 0) {
      waiting.push(`${session.pendingCount} élément${session.pendingCount > 1 ? 's' : ''} à valider`);
    }
    if (session.documentCount === 0) waiting.push('aucun document déposé');

    return (
      <Link
        to="/demarrage"
        className="group flex items-center gap-3 rounded-lg border border-[var(--color-steel)] bg-[var(--color-steel-bg)] px-4 py-2.5 transition-all hover:shadow-[0_0_12px_rgba(122,168,200,0.2)]"
      >
        <ClipboardCheck className="w-4 h-4 text-[var(--color-steel)] shrink-0" />
        <span className="min-w-0 flex-1 text-sm text-[var(--foreground)] truncate">
          <span className="font-medium">Intégration en cours</span>
          {step && <span className="text-[var(--muted-foreground)]"> — {step.label}</span>}
          {waiting.length > 0 && (
            <span className="text-[var(--muted-foreground)]"> · {waiting.join(' · ')}</span>
          )}
        </span>
        <span className="inline-flex items-center gap-1 text-sm font-medium text-[var(--color-steel)] shrink-0">
          Reprendre
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
        </span>
      </Link>
    );
  }

  // --- 3. Blocking findings: sales that decrement nothing don't sit quietly --
  if (blockingFindings > 0) {
    return (
      <Link
        to="/demarrage"
        className="group flex items-center gap-3 rounded-lg border border-[var(--color-brick-border)] bg-[var(--color-brick-bg)] px-4 py-2.5 transition-all hover:shadow-sm"
      >
        <AlertOctagon className="w-4 h-4 text-[var(--color-brick)] shrink-0" />
        <span className="min-w-0 flex-1 text-sm text-[var(--color-brick)] truncate">
          <span className="font-medium">
            {blockingFindings} constat{blockingFindings > 1 ? 's' : ''} bloquant{blockingFindings > 1 ? 's' : ''}
          </span>{' '}
          au rapport d'exploitation — des ventes pourraient ne rien décrémenter.
        </span>
        <span className="inline-flex items-center gap-1 text-sm font-medium text-[var(--color-brick)] shrink-0">
          Voir le rapport
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
        </span>
      </Link>
    );
  }

  return null;
}
